import { Modal } from "antd";
import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import LoadingIndicator from "components/ui/LoadingIndicator";
import { useFeatureFlag } from "hooks/ui";
import { getCurrentUser } from "legacy/selectors/usersSelectors";
import { useAppSelector } from "store/helpers";
import { Flag } from "store/slices/featureFlags";
import { callServer, HttpMethod } from "store/utils/client";
import logger from "utils/logger";
import {
  NotificationPosition,
  sendSuccessUINotification,
  sendErrorUINotification,
} from "utils/notification";
import GridBg from "./GridBg";
import VideoStillImg from "./assets/video-still.jpeg";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: 40px 28px 32px 28px;

  border-radius: 24px;

  background:
    url(${GridBg}) repeat,
    radial-gradient(42.93% 42.93% at 50% 50%, #383493 0%, #191748 100%),
    linear-gradient(0deg, #191748, #191748);
  background-size:
    816px 231px,
    100% 100%,
    100% 100%;
  background-repeat: repeat, no-repeat, no-repeat;
  background-position:
    top left,
    center,
    center;

  box-shadow:
    0px 24px 24px 0px rgba(16, 15, 46, 0.2),
    0px 8px 8px 0px rgba(16, 15, 46, 0.2),
    0px 2px 2px 0px rgba(16, 15, 46, 0.2),
    0px 0px 0px 6px rgba(0, 0, 122, 0.2);
`;

const Heading = styled.h1`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 30px;
  font-weight: 600;
  margin-bottom: 0.05em;
  text-align: center;
  color: white;
`;

const Subheading = styled.h2`
  font-size: 19px;
  font-weight: 400;
  margin-bottom: 32px;
  text-align: center;
  color: white;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 605px;
  min-height: 300px;
  margin: 0 auto 2em auto;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  .video-overlay {
    position: absolute;
    inset: 0;
    z-index: 3;
    background: transparent;
    pointer-events: auto;

    &[data-has-played="false"] {
      background: rgba(25, 23, 72, 0.4);
    }
  }

  img {
    max-width: 100%;
    height: auto;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 2;
    border-radius: 18px;

    &[data-should-hide="true"] {
      display: none;
    }
  }

  .pause-button {
    display: none;
  }

  &[data-is-playing="true"]:hover {
    .pause-button {
      display: flex;
    }
  }
`;

const PlayPauseButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  background: rgba(15, 15, 16, 0.8);
  backdrop-filter: blur(4px)
  box-shadow: 0px 8px 32px -6px rgba(255, 255, 255, 0.4), 0px 0px 0px 1px rgba(255, 255, 255, 0.16);

  &:hover {
    background: rgba(15, 15, 16, 0.9);
  }
`;

const PlayButton = styled(PlayPauseButton)`
  &[data-is-playing="true"] {
    display: none;
  }
`;

const IframeWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  border-radius: 18px;
  overflow: hidden;
`;

const VideoText = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 88px;
  z-index: 10;
  width: auto;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  color: white;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(64px);
  padding: 3px 10px;
  border-radius: 100px;
  display: inline-block;
  white-space: nowrap;
`;

const RequestButton = styled.div`
  cursor: pointer;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 20px;
  gap: 10px;
  width: auto;
  height: 48px;
  margin-top: 4px;
  color: rgba(255, 255, 255, 1);
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  border-radius: 10px;

  background: linear-gradient(0deg, #6647ff, #6647ff),
    radial-gradient(
      124.62% 63.54% at 50.26% 0%,
      rgba(255, 255, 255, 0.09) 0%,
      rgba(255, 255, 255, 0) 47.4%
    );

  border: 1px solid transparent;

  /* Replace border-image-source with a complete border-image property */
  border-image: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0.016) 100%
    )
    1;

  /* Alternative styling for Chrome compatibility */
  position: relative;
  z-index: 1;

  &:before {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: 10px;
    padding: 1px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0.016) 100%
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: -1;
  }

  box-shadow:
    0px 1px 2px 0px rgba(27, 30, 34, 0.08),
    0px 0px 0px 1.5px rgba(0, 0, 0, 0.6);

  &:hover {
    opacity: 0.9;
  }

  .right-arrow {
    position: relative;
    top: 1px;
  }
`;

const CloseBtn = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 100px;
  border-width: 1px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  margin-top: 12px;
  position: relative;
  right: 14px;
`;

const SHOW_CLARK_SIGNUP_MODAL_KEY = "showClarkSignupModal";

const modalBodyStyle = {
  padding: "0px",
};

const maskStyle = {
  background: "#191748",
  opacity: 0.5,
};

const EXCLUDED_EMAIL_DOMAINS = ["gmail.com", "hotmail.com", "yahoo.com"];

const IntroducingClarkModal: React.FC = () => {
  const isClarkSignupModalEnabled = useFeatureFlag(
    Flag.SHOW_CLARK_SIGNUP_MODAL,
  );
  const currentUser = useAppSelector(getCurrentUser);
  const [shouldShowModal, setShouldShowModal] = useState<boolean>(
    window.localStorage.getItem(SHOW_CLARK_SIGNUP_MODAL_KEY) !== "false",
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Dynamically load Vimeo Player API
  useEffect(() => {
    if (!(window as any).Vimeo) {
      const script = document.createElement("script");
      script.src = "https://player.vimeo.com/api/player.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const handleRequestEarlyAccess = () => {
    const run = async () => {
      try {
        setIsLoading(true);
        await callServer<null>({
          method: HttpMethod.Post,
          url: "v3/home/clark-ai-waitlist-signup",
        });

        sendSuccessUINotification({
          message:
            "You're on the waitlist! We'll be in touch soon with next steps to access Clark",
          key: "introducing-clark-success",
          placement: NotificationPosition.top,
          style: {
            width: "540px",
            animationDuration: "0s",
          },
          duration: 6,
        });
        window.localStorage.setItem(SHOW_CLARK_SIGNUP_MODAL_KEY, "false");
        setShouldShowModal(false);
      } catch (err) {
        logger.error(`Failed to sign up for Clark AI waitlist: ${err}`);
        sendErrorUINotification({
          message:
            "Error signing up for the Clark waitlist. Please try again. Contact support if the error persists.",
          key: "introducing-clark-error",
          placement: NotificationPosition.top,
          style: {
            width: "540px",
            animationDuration: "0s",
          },
          duration: 6,
        });
      } finally {
        setIsLoading(false);
      }
    };

    run();
  };

  const handlePlay = () => {
    setIsPlaying(true);
    setHasPlayed(true);
    // Use Vimeo Player API to play the video
    if (
      iframeRef.current &&
      (window as any).Vimeo &&
      (window as any).Vimeo.Player
    ) {
      const player = new (window as any).Vimeo.Player(iframeRef.current);
      player.play();
    }
  };

  const handleClose = () => {
    window.localStorage.setItem(SHOW_CLARK_SIGNUP_MODAL_KEY, "false");
    setShouldShowModal(false);
  };

  if (
    !isClarkSignupModalEnabled ||
    shouldShowModal === false ||
    EXCLUDED_EMAIL_DOMAINS.includes(currentUser?.email.split("@")[1] ?? "")
  ) {
    return null;
  }

  return (
    <Modal
      open={shouldShowModal}
      wrapClassName="introducing-clark-modal"
      maskClosable
      width="605px"
      footer={null}
      afterClose={handleClose}
      maskStyle={maskStyle}
      bodyStyle={modalBodyStyle}
      closeIcon={
        <CloseBtn onClick={handleClose}>
          <CloseIcon />
        </CloseBtn>
      }
    >
      <Container>
        <ClarkIcon />
        <Heading>Introducing Clark</Heading>
        <Subheading>
          The first AI Agent to build Internal Enterprise Apps
        </Subheading>
        <VideoContainer data-is-playing={isPlaying}>
          <IframeWrapper>
            <iframe
              ref={iframeRef}
              src="https://player.vimeo.com/video/1086922596?controls=1&fullscreen=1&title=0&byline=0&portrait=0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              style={{
                position: "absolute",
                top: 0,
                left: "4px",
                width: "calc(100% - 8px)",
                height: "100%",
                border: 0,
                zIndex: 1,
                pointerEvents: hasPlayed ? undefined : "none",
              }}
              title="Superblocks - Empowering the next billion developers"
            />
          </IframeWrapper>
          <div
            className="video-overlay"
            data-has-played={hasPlayed}
            style={{
              display: hasPlayed ? "none" : undefined,
            }}
          />
          <img
            alt="Introducing Clark"
            src={VideoStillImg}
            loading="eager"
            style={{ zIndex: 2 }}
            data-should-hide={hasPlayed}
            onClick={handlePlay}
          />
          <VideoText style={{ display: hasPlayed ? "none" : "inline-block" }}>
            Watch the 2 minute Clark AI overview with Brad, our CEO
          </VideoText>
          <PlayButton onClick={handlePlay} data-is-playing={isPlaying}>
            <PlayIcon />
          </PlayButton>
        </VideoContainer>

        <RequestButton onClick={handleRequestEarlyAccess}>
          {isLoading && <LoadingIndicator useWhiteBackground={true} />}
          <span>
            Request early access <span className="right-arrow">→</span>
          </span>
        </RequestButton>
      </Container>
    </Modal>
  );
};

export default IntroducingClarkModal;

const PlayIcon = () => (
  <svg
    width="12"
    height="17"
    viewBox="0 0 12 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.833252 14.2425V2.75767C0.833252 1.92048 0.833252 1.50189 1.00689 1.27691C1.15806 1.08104 1.38845 0.96248 1.63571 0.953311C1.9197 0.942781 2.26032 1.18608 2.94157 1.67269L10.9809 7.4151C11.4932 7.78101 11.7494 7.96396 11.84 8.19081C11.9194 8.38936 11.9194 8.61081 11.84 8.80935C11.7494 9.0362 11.4932 9.21915 10.981 9.58506L2.94157 15.3275C2.26032 15.8141 1.9197 16.0574 1.63571 16.0469C1.38845 16.0377 1.15806 15.9191 1.00689 15.7233C0.833252 15.4983 0.833252 15.0797 0.833252 14.2425Z"
      fill="white"
    />
  </svg>
);

const ClarkIcon = () => (
  <svg
    width="60"
    height="45"
    viewBox="0 0 60 45"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M40.0144 3.27992C43.9358 0.494604 52.0382 -1.01626 58.1427 6.10566C58.9269 7.02056 59.0983 8.44027 58.0167 9.19852C58.001 9.20956 57.9851 9.22031 57.9693 9.23077C58.6392 10.3233 59.4411 11.954 59.6579 14.1222C59.7561 15.1042 59.2804 16.0583 58.2945 16.3286C58.1578 17.9315 57.7573 21.0812 55.1849 23.7585C54.067 24.9221 53.0072 25.7255 52.0743 26.2752C51.3114 26.7249 51.1048 27.0606 51.074 27.9175C50.9471 31.4511 49.7594 35.1789 47.4729 37.8606C44.6081 41.2205 39.5388 43.0486 33.8859 43.81C28.2225 44.5727 22.034 43.796 17.8141 40.4627C15.0229 38.2578 13.3734 35.2677 12.5607 32.1179C12.3364 31.2484 12.0468 30.9709 11.1601 30.7035C10.0165 30.3586 8.60523 29.7143 7.01485 28.559C4.01095 26.3769 3.06155 23.3471 2.64451 21.7934C-0.538418 21.5046 1.03088 16.4951 1.71378 14.7493C0.474053 14.2185 0.371267 12.7601 0.991954 11.7043C5.74574 3.61805 13.9879 3.67669 18.3388 5.7273C20.0802 6.54801 21.6515 7.52705 22.9287 8.89872C25.1077 11.2387 26.9406 6.94355 28.7951 6.07814C31.0545 5.02375 30.2684 6.69165 30.452 7.28316C30.6199 6.88316 33.7658 5.325 33.6152 6.83128C33.521 7.77312 33.5612 8.37907 34.1673 8.64886C34.8832 8.96755 35.3741 8.44785 35.6828 7.88029C36.7306 5.95389 38.2264 4.54989 40.0144 3.27992ZM22.5506 21.7789C24.1618 21.2701 25.5039 22.4879 25.7086 24.0669C25.9133 25.6459 25.2252 26.7415 24.0329 27.0638C22.8406 27.386 21.5194 26.2582 21.2616 25.1625C21.1654 24.7534 21.0961 24.2491 21.1309 23.7512C21.1491 23.4904 21.5293 23.4015 21.7907 23.4015C22.2539 23.4015 22.5828 22.9233 22.4289 22.4946C22.3406 22.2489 22.3016 21.8575 22.5506 21.7789ZM44.0571 20.1163C43.773 18.5497 42.3712 17.4011 40.7877 17.9906C40.543 18.0816 40.6017 18.4706 40.7022 18.7115C40.8776 19.1319 40.5732 19.6261 40.1107 19.6495C39.8496 19.6627 39.4743 19.7706 39.4692 20.032C39.4596 20.531 39.5543 21.0312 39.671 21.4349C39.9837 22.5161 41.3602 23.5759 42.5347 23.1939C43.7093 22.812 44.3412 21.683 44.0571 20.1163ZM30.2789 32.2141C29.6937 31.4317 29.3185 30.4752 29.2482 29.4199L28.9539 25.005C28.7894 22.5366 30.4786 20.3281 32.9039 19.8406C35.5482 19.3092 38.129 20.9998 38.698 23.6362L39.6315 27.9613C39.931 29.3492 39.653 30.7219 38.9637 31.8355C39.0779 34.107 37.2974 36.1867 34.8479 36.2307C32.4863 36.2731 30.5288 34.4981 30.2789 32.2141ZM8.89214 19.9306C8.35552 16.9334 10.3502 14.0687 13.3474 13.5321C14.6694 13.2954 15.9656 13.5511 17.0476 14.1682C17.3605 14.3466 17.3961 14.7676 17.1576 15.0375C15.2197 17.2309 13.5269 20.0613 12.7435 23.5261C12.6632 23.8814 12.2951 24.101 11.9658 23.9452C10.4086 23.2087 9.21887 21.7555 8.89214 19.9306ZM51.4507 16.8004C52.2387 13.8593 50.4933 10.8362 47.5522 10.0482C45.7615 9.56835 43.9405 10.0277 42.6119 11.1242C42.331 11.3561 42.3734 11.7826 42.6608 12.0063C45.4641 14.1879 47.3068 16.9233 48.4686 19.6096C48.6116 19.9402 49.0078 20.0869 49.3023 19.8795C50.3207 19.1623 51.1031 18.0977 51.4507 16.8004Z"
      fill="white"
    />
    <path
      d="M30.2788 32.2177C31.4091 33.7293 33.3236 34.5916 35.34 34.2626C36.9073 34.0069 38.1928 33.0857 38.9639 31.8403C39.0795 34.1128 37.2986 36.1941 34.8482 36.2381C32.4853 36.2805 30.527 34.5035 30.2788 32.2177Z"
      fill="white"
      fillOpacity="0.4"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 5L9 1M5 5L1 1M5 5L1 9M5 5L9 9"
      stroke="white"
      strokeOpacity="0.3"
      strokeWidth="1.5"
    />
  </svg>
);
